import { arrowDown } from "helper/icons";
import { cross } from "helper/images";
import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

const Dropdown = (props) => {
  const {
    dropdownOptions,
    width = "w-[358px] lg:w-[384px] max-[400px]:w-[100%]",
    placeholder,
    showIconLeft = false,
    checkValidation,
    defaultOption = "",
    onChange,
    label,
    errorMsg,
    disabled = false,
    isFaded,
    dynamicHeight = false,
    className = "",
    customContent,
    mainContent,
    type = "primary",
    transform = "",
    showCross = false,
    isShadow = false,
  } = props || {};

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedOption(defaultOption);
  }, [defaultOption]);

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setIsOpen((prev) => !prev);
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const selectOption = (option, index) => {
    setSelectedOption(option);
    setIsOpen(false);
    onChange?.(option, index);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getClasses = () => {
    return `${width} flex ${
      !selectedOption && checkValidation ? "border-red" : ""
    } ${showIconLeft ? "flex-row-reverse" : "flex-row"} ${
      showIconLeft ? "justify-end" : customContent ? customContent : "justify-between"
    } items-center rounded-md border ${
      isShadow ? "shadow-100" : ""
    } px-2.5 py-2.5 bg-white text-base font-regular text-gray-500 inputFocus focus:outline-none ${className}`;
  };

  const BtnComponent = () => {
    if (type !== "primary") {
      return (
        <div
          ref={buttonRef}
          className={`${width} flex justify-center align-center cursor-pointer`}
          onClick={toggleDropdown}
        >
          <span
            className={`text-sm font-regular ${
              selectedOption ? "text-gray-500" : "text-gray-700"
            } max-[400px]:text-xs`}
          >
            {selectedOption || placeholder}
          </span>
          {!disabled && (
            <img
              src={arrowDown}
              height={18}
              width={18}
              alt=""
              className={`transform rotate-180 ml-2`}
            />
          )}
          {showCross && selectedOption && (
            <img
              onClick={() => selectOption("", 0)}
              src={cross}
              height={24}
              width={24}
              alt=""
              className="ml-2"
            />
          )}
        </div>
      );
    }

    return (
      <button
        type="button"
        ref={buttonRef}
        className={getClasses()}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span
          className={selectedOption !== "" ? "" : "text-gray opacity-[0.7]"}
        >
          {selectedOption || placeholder}
        </span>
        {!disabled && (
          <img
            src={arrowDown}
            height={20}
            width={20}
            alt=""
            className={`transform rotate-180 ${showIconLeft ? "mr-[5px]" : ""}`}
          />
        )}
        {showCross && selectedOption && (
          <img
            onClick={() => selectOption("", 0)}
            src={cross}
            height={24}
            width={24}
            alt=""
            className="absolute right-2"
          />
        )}
      </button>
    );
  };

  return (
    <div className={`relative ${mainContent} z-200 ${width}`}>
      {!!label && (
        <div className="mb-0.5">
          <span className="text-gray-700 font-medium text-sm">{label}</span>
        </div>
      )}
      <BtnComponent />

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`absolute bg-white rounded-md border shadow-100 z-50 ${
              !dynamicHeight ? "max-h-[190px]" : ""
            } ${dropdownOptions?.length > 3 ? "overflow-y-scroll" : ""}`}
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            <div
              className="py-1"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              {dropdownOptions.map((option, index) => (
                <button
                  key={option}
                  onClick={(event) => {
                    event.stopPropagation();
                    selectOption(option, index);
                  }}
                  className={`block w-full text-left px-4 py-4 text-sm text-gray-700 hover:bg-primary/50 hover:text-white ${
                    index !== dropdownOptions?.length - 1
                      ? "border-b border-purple-100"
                      : ""
                  } max-[400px]:text-xs`}
                  role="menuitem"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}

      {!!errorMsg && (
        <span className="text-red font-medium text-sm mt-1 pt-2">
          {errorMsg}
        </span>
      )}
    </div>
  );
};

export default Dropdown;
